// File: src/pages/CashBookReports.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
  Label,
} from "reactstrap";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import swal from "sweetalert";
import { FaFilter } from "react-icons/fa";
import { ImDownload } from "react-icons/im";
import { AiOutlineFilePdf } from "react-icons/ai";

import { _Post, _Get } from "../import/ApiEndPoint/ApiCalling";
import {
  Cashbook_Report,
  BankAccount_Report,
  View_Company_Details,
  View_Receipt,
} from "../import/ApiEndPoint/Api";
import { CheckPermission } from "../import/CheckPermission";
import { baseURL } from "../import/ApiEndPoint/TestApi";
import RupioSoftLogo from "../import/logo.png";

/* -------------------- constants -------------------- */
const CASH_ID = "CASH";
const CASH_TRANSFER_USER_SID = "CASH7845";

/* -------------------- helpers -------------------- */
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const norm = (v) =>
  String(v || "")
    .trim()
    .toLowerCase();
const fmtDate = (s) => (s ? String(s).split("T")[0] : "");

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmtAmt = (v) => {
  const n = toNum(v);
  return n ? n.toFixed(2) : "";
};

const unwrapApiResponse = (response) => {
  if (response?.data && typeof response.data === "object") {
    return response.data;
  }
  return response && typeof response === "object" ? response : {};
};

const firstArray = (...values) => {
  let firstEmptyArray = null;

  for (const value of values) {
    if (!Array.isArray(value)) continue;
    if (value.length) return value;
    if (!firstEmptyArray) firstEmptyArray = value;
  }

  return firstEmptyArray || [];
};

const getTransactionDate = (item = {}) =>
  fmtDate(
    item?.date ||
      item?.invoiceDate ||
      item?.purchaseDate ||
      item?.receiptDate ||
      item?.paymentDate ||
      item?.createdAt ||
      item?.updatedAt,
  );

const getTransactionAmount = (item = {}) =>
  toNum(
    item?.amount ??
      item?.Amount ??
      item?.total ??
      item?.Total ??
      item?.grandTotal ??
      item?.GrandTotal ??
      item?.netAmount ??
      item?.totalAmount ??
      item?.payableAmount ??
      item?.paidAmount ??
      item?.debitAmount ??
      item?.debit ??
      item?.creditAmount ??
      item?.credit,
  );

const getUser = () => JSON.parse(localStorage.getItem("userData") || "{}");

const getSelectedDatabase = () => {
  const user = getUser();
  const financialYearSelected = localStorage.getItem("financialYearSelected");

  if (!user?.database) return "";

  return financialYearSelected
    ? `${user.database}-${financialYearSelected}`
    : user.database;
};

const normalizeFinancialYear = (value) => {
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{4})\s*-\s*(\d{2}|\d{4})$/);
  if (!match) return raw;

  const startYear = match[1];
  const endYear = match[2].length === 4 ? match[2].slice(-2) : match[2];

  return `${startYear}-${endYear}`;
};

const getFYStartYear = (fy) => {
  const start = Number(String(fy || "").split("-")[0]);
  return Number.isFinite(start) ? start : 0;
};

const getCurrentFinancialYear = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth(); // Jan = 0, Apr = 3
  const start = m >= 3 ? y : y - 1;
  return `${start}-${String(start + 1).slice(-2)}`;
};

const getSelectedFinancialYear = () =>
  normalizeFinancialYear(
    localStorage.getItem("financialYearSelected") || getCurrentFinancialYear(),
  );

const getPreviousFinancialYear = (fy) => {
  const start = getFYStartYear(fy);
  if (!start) return "";
  return `${start - 1}-${String(start).slice(-2)}`;
};

const buildDatabaseForFinancialYear = (db, fy) => {
  const cleanDb = String(db || "").replace(/-\d{4}-\d{2}$/, "");
  const cleanFY = normalizeFinancialYear(fy);
  return cleanFY ? `${cleanDb}-${cleanFY}` : cleanDb;
};

const getBaseDatabase = (db) =>
  String(db || "")
    .trim()
    .replace(/-\d{4}-\d{2}$/, "");

const getReceiptPartyObject = (r = {}) => {
  const candidates = [
    r?.partyId,
    r?.party,
    r?.customerId,
    r?.customer,
    r?.partyDetails,
    r?.customerDetails,
  ];

  return (
    candidates.find(
      (value) => value && typeof value === "object" && !Array.isArray(value),
    ) || null
  );
};

const isCashTransferParty = (r = {}, baseDatabase = "") => {
  const party = getReceiptPartyObject(r);
  if (!party) return false;

  const companyName = norm(
    party?.CompanyName || party?.companyName || party?.name,
  );
  const sid = String(party?.sId || party?.sid || "")
    .trim()
    .toUpperCase();
  const partyDatabase = getBaseDatabase(
    party?.database || party?.Database || party?.db,
  );
  const expectedDatabase = getBaseDatabase(baseDatabase);

  return (
    companyName === "cash" &&
    sid === CASH_TRANSFER_USER_SID &&
    Boolean(expectedDatabase) &&
    partyDatabase === expectedDatabase
  );
};

const isReceiptEntry = (r = {}) => {
  const type = String(
    r?.type ||
      r?.voucherType ||
      r?.receiptType ||
      r?.transactionType ||
      r?.entryType ||
      "",
  )
    .trim()
    .toLowerCase();

  return !type || type.includes("receipt") || type.includes("reciept");
};

const isReceiptToBank = (r = {}) => {
  const toAccountId = String(getToAccountIdFromRow(r) || "")
    .trim()
    .toUpperCase();

  if (toAccountId && toAccountId !== CASH_ID) return true;

  const bankName = norm(
    r?.bankDetails?.bankName ||
      (Array.isArray(r?.bankDetails) ? r?.bankDetails?.[0]?.bankName : "") ||
      r?.bankName ||
      r?.bankSelect,
  );

  if (bankName && bankName !== "cash") return true;

  const mode = norm(
    r?.paymentMode ||
      r?.modeOfPayment ||
      r?.paymentType ||
      r?.mode ||
      r?.PaymentMode,
  );

  return mode === "bank";
};

const isSpecialCashToBankReceipt = (r = {}, baseDatabase = "") =>
  isReceiptEntry(r) &&
  isCashTransferParty(r, baseDatabase) &&
  isReceiptToBank(r);

const normalizeFinancialYears = (value) => {
  let list = value;

  if (typeof list === "string") {
    try {
      list = JSON.parse(list);
    } catch {
      list = list
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
    }
  }

  if (!Array.isArray(list)) return [];

  return [
    ...new Set(list.map((x) => normalizeFinancialYear(x)).filter(Boolean)),
  ].sort((a, b) => getFYStartYear(a) - getFYStartYear(b));
};

const getFinancialYearsForCarryForward = (companyDetail = {}, user = {}) => {
  const companyYears = normalizeFinancialYears(companyDetail?.financialYears);
  const userYears = normalizeFinancialYears(user?.financialYears);

  return companyYears.length ? companyYears : userYears;
};

const balanceToOpening = (balance) => {
  const n = toNum(balance);
  return {
    openingBalance: Math.abs(n),
    openingType: n < 0 ? "credit" : "debit",
  };
};

const normalizeKey = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/\([^)]*\)/g, " ")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeId = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (typeof v === "object") return String(v?._id || v?.id || "");
  return String(v);
};

const getPartyText = (party) => {
  if (!party) return "";
  if (typeof party === "string") return party;
  if (typeof party === "object") {
    return (
      party?.CompanyName ||
      party?.companyName ||
      party?.partyName ||
      party?.name ||
      party?.title ||
      ""
    );
  }
  return String(party);
};

const isCashParty = (party) => norm(getPartyText(party)) === "cash";
const isCashMode = (mode) => norm(mode) === "cash";
const isBankMode = (mode) => norm(mode) === "bank";

const getInvoiceLikeNumber = (item = {}) =>
  item?.invoiceNo ||
  item?.invoiceId ||
  item?.invoiceNumber ||
  item?.billNo ||
  item?.voucherNo ||
  item?.orderNo ||
  item?.no ||
  item?.id ||
  "";

const resolveParticular = (item = {}) => {
  if (item?.expenseId?.title) return item.expenseId.title;
  if (item?.partyId?.CompanyName) return item.partyId.CompanyName;
  if (item?.userId?.firstName) {
    return `${item.userId.firstName}${item.userId.lastName ? ` ${item.userId.lastName}` : ""}`;
  }
  if (item?.transporterId?.companyName) return item.transporterId.companyName;
  if (item?.party) return item.party;
  return item?.particular || "N/A";
};

const isCashLikeName = (value) => {
  const s = norm(getPartyText(value));
  return (
    s === "cash" ||
    s === "cash account" ||
    s === "cash a/c" ||
    s === "cash in hand" ||
    s.includes("cash")
  );
};

const isCashTransaction = (item = {}) => {
  const mode =
    item?.paymentMode ||
    item?.modeOfPayment ||
    item?.paymentType ||
    item?.mode ||
    item?.PaymentMode;

  const party =
    item?.party ||
    item?.partyName ||
    item?.customer ||
    item?.customerName ||
    item?.supplier ||
    item?.supplierName ||
    item?.partyId;

  const hasCashFlag =
    item?.isCash === true ||
    item?.cashSale === true ||
    item?.cashPurchase === true;

  const hasClassifier = Boolean(
    String(mode || "").trim() || getPartyText(party).trim() || hasCashFlag,
  );

  // Cashbook APIs often already return only cash transactions and may omit the
  // party/payment-mode fields. In that case, do not discard valid API rows.
  if (!hasClassifier) return true;

  return (
    isCashMode(mode) ||
    isCashParty(party) ||
    isCashLikeName(party) ||
    hasCashFlag
  );
};

const getUserSid = (u) =>
  String(u?.sId || u?.sid || u?.userId || "")
    .trim()
    .toUpperCase();

const getReceiptType = (r) => {
  const t = String(
    r?.type ||
      r?.voucherType ||
      r?.receiptType ||
      r?.transactionType ||
      r?.entryType ||
      r?.paymentReceiptType ||
      "",
  )
    .trim()
    .toLowerCase();

  if (t.includes("receipt") || t.includes("reciept")) return "receipt";
  if (t.includes("payment")) return "payment";
  if (r?.isReceipt === true) return "receipt";
  if (r?.isPayment === true) return "payment";
  if (toNum(r?.debit) > 0 || toNum(r?.debitAmount) > 0) return "receipt";
  if (toNum(r?.credit) > 0 || toNum(r?.creditAmount) > 0) return "payment";
  return "";
};

const getToAccountIdFromRow = (r) => {
  const bd = r?.bankDetails;
  if (!bd) return "";
  if (typeof bd === "string") return bd;
  if (Array.isArray(bd)) {
    const x = bd[0];
    if (typeof x === "string") return x;
    if (x && typeof x === "object") return normalizeId(x);
    return "";
  }
  if (typeof bd === "object") return normalizeId(bd);
  return "";
};

const bankLabel = (b) => {
  if (!b) return "Bank";
  const isCash = String(b?._id || "") === CASH_ID || b?.isCash === true;
  if (isCash) return "Cash";
  const name = b?.bankName || "Bank";
  const acc = b?.accountNumber ? ` (${b.accountNumber})` : "";
  return `${name}${acc}`;
};

const getToAccountNameFromRow = (r, accountsById) => {
  const toId = getToAccountIdFromRow(r);
  if (String(toId).toUpperCase() === CASH_ID) return "Cash";

  if (r?.bankSelect) return String(r.bankSelect);

  const bd = r?.bankDetails;
  if (bd && typeof bd === "object" && !Array.isArray(bd) && bd?.bankName) {
    return bd.bankName;
  }
  if (
    Array.isArray(bd) &&
    bd[0] &&
    typeof bd[0] === "object" &&
    bd[0]?.bankName
  ) {
    return bd[0].bankName;
  }

  const acc = accountsById.get(String(toId));
  if (acc?.bankName) return String(acc.bankName);

  return "Unknown";
};

const getParticularDisplay = (r, accountsById, accountsByName) => {
  const t = String(r?.particularAccountType || "")
    .trim()
    .toLowerCase();

  if (t === "cash") return "Cash";

  if (t === "bank") {
    const id = normalizeId(r?.expenseId);
    const acc = accountsById.get(String(id));
    return acc ? bankLabel(acc) : "Bank";
  }

  const n =
    r?.expenseId?.title ||
    r?.expenseId?.bankName ||
    r?.partyId?.CompanyName ||
    (r?.userId
      ? `${r?.userId?.firstName || ""} ${r?.userId?.lastName || ""}`.trim()
      : "") ||
    r?.transporterId?.companyName ||
    "";

  const exact = accountsByName.get(normalizeKey(n));
  if (exact) return bankLabel(exact);

  return n || "NA";
};

const resolveFromAccountId = (r, accountsByName) => {
  const t = String(r?.particularAccountType || "")
    .trim()
    .toLowerCase();

  if (t === "cash") return CASH_ID;
  if (t === "bank") return String(normalizeId(r?.expenseId) || "");

  const particularText =
    r?.expenseId?.title ||
    r?.expenseId?.bankName ||
    r?.partyId?.CompanyName ||
    (r?.userId
      ? `${r?.userId?.firstName || ""} ${r?.userId?.lastName || ""}`.trim()
      : "") ||
    r?.transporterId?.companyName ||
    "";

  const match = accountsByName.get(normalizeKey(particularText));
  return match?._id ? String(match._id) : "";
};

const formatBalanceText = (v) => {
  const n = toNum(v);
  if (!n) return "0.00";
  return n >= 0 ? `${n.toFixed(2)} Dr` : `${Math.abs(n).toFixed(2)} Cr`;
};

const loadImageAsDataUrl = (url) =>
  new Promise((resolve) => {
    if (!url) {
      resolve(null);
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(null);
          return;
        }

        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch (error) {
        console.warn("[CashBookReports] image conversion failed:", error);
        resolve(null);
      }
    };

    img.onerror = () => resolve(null);
    img.src = url;
  });

const getCompanyLogoUrl = (company = {}) => {
  const logo =
    company?.logo ||
    company?.Logo ||
    company?.companyLogo ||
    company?.CompanyLogo ||
    "";

  if (!logo) return "";

  const value = String(logo).trim();
  if (/^(https?:|data:|blob:)/i.test(value)) return value;

  const cleanBase = String(baseURL || "").replace(/\/$/, "");
  const cleanLogo = value.replace(/^\//, "");

  if (/^Images\//i.test(cleanLogo)) {
    return `${cleanBase}/${cleanLogo}`;
  }

  return `${cleanBase}/Images/${cleanLogo}`;
};

const getCompanyField = (company = {}, ...keys) => {
  for (const key of keys) {
    const value = company?.[key];
    if (value !== null && value !== undefined && String(value).trim()) {
      return String(value).trim();
    }
  }
  return "";
};

const extractCashOpening = ({ cashResponse, companyDetail, user }) => {
  const root = unwrapApiResponse(cashResponse);
  const cashBook =
    root?.CashBook ||
    root?.Cashbook ||
    root?.cashBook ||
    root?.cashbook ||
    root?.result ||
    root;

  const candidates = [
    cashBook?.opening,
    cashBook?.openingBalance,
    cashBook?.cashOpening,
    cashBook?.cashUser,
    root?.User,
    root?.user,
    companyDetail?.cashOpening,
    companyDetail?.cashUser,
    companyDetail?.cashAccount,
    companyDetail,
    user?.cashOpening,
    user?.cashUser,
    user,
  ].filter(Boolean);

  for (const candidate of candidates) {
    if (typeof candidate === "number" || typeof candidate === "string") {
      return {
        openingBalance: Math.abs(toNum(candidate)),
        openingType: toNum(candidate) < 0 ? "credit" : "debit",
      };
    }

    const rawBalance =
      candidate?.OpeningBalance ??
      candidate?.openingBalance ??
      candidate?.cashOpeningBalance ??
      candidate?.cashBalance;

    if (rawBalance !== undefined && rawBalance !== null && rawBalance !== "") {
      const rawType = String(
        candidate?.Type ||
          candidate?.type ||
          candidate?.openingType ||
          candidate?.balanceType ||
          "debit",
      ).toLowerCase();

      const numericBalance = toNum(rawBalance);
      return {
        openingBalance: Math.abs(numericBalance),
        openingType:
          rawType === "credit" || numericBalance < 0 ? "credit" : "debit",
      };
    }
  }

  return { openingBalance: 0, openingType: "debit" };
};

/* -------------------- css -------------------- */
const injectOnce = () => {
  if (document.getElementById("cashbook-inline-css-v5")) return;
  const style = document.createElement("style");
  style.id = "cashbook-inline-css-v5";
  style.innerHTML = `
    .cb-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .cb-table-wrap{
      width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff;
    }
    .cb-table{
      width:100%; border-collapse:collapse; table-layout:auto; min-width:1200px;
    }
    .cb-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:800; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:8px 10px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .cb-table tbody td, .cb-table tfoot td{
      padding:7px 10px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .cb-table tbody tr:hover{ background:#f9fbff; }
    .cb-table tfoot td{
      background:#f8fafc; font-weight:800; border-top:2px solid #dbeafe;
    }
    .big-num{
      font-size:15px;
      font-weight:800;
      letter-spacing:.01em;
    }
    .align-right{ text-align:right !important; }
    .align-left{ text-align:left !important; }
    .opening-row{
      background:#fefce8;
      font-weight:700;
    }
    .total-row{
      background:#eff6ff;
      font-weight:800;
    }
    .cb-export-btn{
      height:32px; display:inline-flex; align-items:center; justify-content:center; gap:6px;
      padding:0 10px; font-weight:700; white-space:nowrap;
    }
    .listbox{
      border:1px solid #e5e7eb; border-radius:10px; max-height:280px; overflow:auto; background:#fff; padding:8px;
    }
    .pick{
      padding:8px 10px; border-radius:8px; border:1px solid #e5e7eb; margin-bottom:6px; background:#fff;
    }
  `;
  document.head.appendChild(style);
};

/* -------------------- row builders -------------------- */
const normalizeSalesRow = (item = {}) => {
  const invoiceNo = getInvoiceLikeNumber(item);
  return {
    date: getTransactionDate(item),
    type: "sales_cash",
    voucherType: "Sales",
    voucherNo: invoiceNo,
    particular: invoiceNo ? `Cash Sales - ${invoiceNo}` : "Cash Sales",
    remark: item?.remark || item?.remarks || item?.description || "",
    paymentMode: "Cash",
    amount: getTransactionAmount(item),
  };
};

const normalizePurchaseRow = (item = {}) => {
  const invoiceNo = getInvoiceLikeNumber(item);
  return {
    date: getTransactionDate(item),
    type: "purchase_cash",
    voucherType: "Purchase",
    voucherNo: invoiceNo,
    particular: invoiceNo ? `Cash Purchase - ${invoiceNo}` : "Cash Purchase",
    remark: item?.remark || item?.remarks || item?.description || "",
    paymentMode: "Cash",
    amount: getTransactionAmount(item),
  };
};

const normalizeDirectCashBookRow = (item = {}) => {
  const debit = toNum(item?.debit ?? item?.debitAmount);
  const credit = toNum(item?.credit ?? item?.creditAmount);
  const receiptType = getReceiptType(item);
  const rawVoucherType = String(
    item?.voucherType ||
      item?.vchType ||
      item?.transactionType ||
      item?.type ||
      "",
  ).trim();
  const voucherTypeLower = rawVoucherType.toLowerCase();

  let type = "";
  if (debit > 0) type = "debit";
  else if (credit > 0) type = "credit";
  else if (receiptType === "receipt") type = "receipt_cash";
  else if (receiptType === "payment") type = "payment_cash";
  else if (voucherTypeLower.includes("sale")) type = "sales_cash";
  else if (voucherTypeLower.includes("purchase")) type = "purchase_cash";
  else type = "debit";

  return {
    date: getTransactionDate(item),
    type,
    voucherType: rawVoucherType || (debit > 0 ? "Receipt" : "Payment"),
    voucherNo: getInvoiceLikeNumber(item),
    particular:
      item?.particular ||
      item?.particulars ||
      item?.ledgerName ||
      item?.accountName ||
      resolveParticular(item),
    remark: item?.remark || item?.remarks || item?.description || "",
    paymentMode:
      item?.paymentMode || item?.modeOfPayment || item?.paymentType || "Cash",
    amount: debit || credit || getTransactionAmount(item),
  };
};

const getDebitAmount = (r) => {
  const amt = toNum(r?.amount);
  if (
    r?.type === "sales_cash" ||
    r?.type === "receipt_cash" ||
    r?.type === "cash_withdraw_bank" ||
    r?.type === "debit"
  ) {
    return amt;
  }
  return 0;
};

const getCreditAmount = (r) => {
  const amt = toNum(r?.amount);
  if (
    r?.type === "purchase_cash" ||
    r?.type === "payment_cash" ||
    r?.type === "cash_deposit_bank" ||
    r?.type === "credit"
  ) {
    return amt;
  }
  return 0;
};

/* -------------------- component -------------------- */
export default function CashBookReports() {
  useEffect(() => injectOnce(), []);

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [modal, setModal] = useState(false);

  const [permissions, setPermissions] = useState({
    View: true,
    Create: true,
    Edit: true,
    Delete: true,
    Download: true,
  });

  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);

  const [companyBanks, setCompanyBanks] = useState([]);
  const [bankReportRows, setBankReportRows] = useState([]);
  const [cashBookData, setCashBookData] = useState({
    salesData: [],
    purchaseData: [],
    receipts: [],
    directRows: [],
  });
  const [cashOpening, setCashOpening] = useState({
    openingBalance: 0,
    openingType: "debit",
  });
  const [companyDetail, setCompanyDetail] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("Companydetail") || "{}");
    } catch {
      return {};
    }
  });

  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [selectedIndex, setSelectedIndex] = useState(-1);
  const addBufferRef = useRef([]);

  const openModal = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setModal(true);
  };
  const closeModal = () => setModal(false);

  const baseColumns = useMemo(
    () => [
      { key: "date", header: "DATE", get: (r) => r.date || "" },
      {
        key: "particular",
        header: "PARTICULARS",
        tdClass: "align-left",
        get: (r) =>
          r.particular
            ? `${r.particular}${r.remark ? ` - ${r.remark}` : ""}`
            : "",
      },
      {
        key: "voucherType",
        header: "VCH TYPE",
        get: (r) => r.voucherType || "",
      },
      {
        key: "voucherNo",
        header: "VCH NO",
        get: (r) => r.voucherNo || "",
      },
      {
        key: "mode",
        header: "MODE",
        get: (r) => r.paymentMode || "",
      },
      {
        key: "debit",
        header: "DEBIT",
        tdClass: "big-num align-right",
        get: (r) => fmtAmt(getDebitAmount(r)),
      },
      {
        key: "credit",
        header: "CREDIT",
        tdClass: "big-num align-right",
        get: (r) => fmtAmt(getCreditAmount(r)),
      },
      {
        key: "balance",
        header: "BALANCE",
        tdClass: "big-num align-right",
        get: (r) => formatBalanceText(r.cashRunning),
      },
    ],
    [],
  );

  const [selectedKeys, setSelectedKeys] = useState([]);

  useEffect(() => {
    const raw = localStorage.getItem("CashbookCols");
    let next = [];
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        next = parsed.filter((k) => baseColumns.some((c) => c.key === k));
      }
    } catch {}
    if (!next.length) next = baseColumns.map((c) => c.key);
    setSelectedKeys(next);
  }, [baseColumns]);

  const visibleCols = useMemo(
    () =>
      selectedKeys
        .map((k) => baseColumns.find((c) => c.key === k))
        .filter(Boolean),
    [selectedKeys, baseColumns],
  );

  const applyLocalDateFilter = (data, range) => {
    const s = range?.startDate || "";
    const e = range?.endDate || "";
    if (!s && !e) return data;

    return data.filter((r) => {
      if (r?.isOpening) return true;
      if (!r?.date) return false;
      if (s && r.date < s) return false;
      if (e && r.date > e) return false;
      return true;
    });
  };

  const buildRowsWithRunning = ({
    nextCashBookData,
    nextBankReportRows,
    nextCompanyBanks,
    nextCashOpening,
    nextSpecialCashReceipts = [],
    baseDatabase = "",
  }) => {
    const accountsById = new Map();
    const accountsByName = new Map();

    [
      { _id: CASH_ID, bankName: "Cash", isCash: true },
      ...(nextCompanyBanks || []),
    ].forEach((b) => {
      accountsById.set(String(b?._id || ""), b);

      const isCash =
        String(b?._id || "").toUpperCase() === CASH_ID || b?.isCash === true;

      if (isCash) {
        if (!accountsByName.has("cash")) accountsByName.set("cash", b);
      } else {
        const k1 = normalizeKey(b?.bankName);
        const k2 = normalizeKey(bankLabel(b));
        if (k1 && !accountsByName.has(k1)) accountsByName.set(k1, b);
        if (k2 && !accountsByName.has(k2)) accountsByName.set(k2, b);
      }
    });

    const salesEntries = Array.isArray(nextCashBookData?.salesData)
      ? nextCashBookData.salesData
          .filter((x) => isCashTransaction(x))
          .map((x) => normalizeSalesRow(x))
      : [];

    const purchaseEntries = Array.isArray(nextCashBookData?.purchaseData)
      ? nextCashBookData.purchaseData
          .filter((x) => isCashTransaction(x))
          .map((x) => normalizePurchaseRow(x))
      : [];

    const normalizedCashbookReceipts = [];
    const includedReceiptIds = new Set();

    const specialCashReceiptIds = new Set(
      (nextSpecialCashReceipts || [])
        .map((r) => String(r?._id || ""))
        .filter(Boolean),
    );

    (nextCashBookData?.receipts || []).forEach((r) => {
      const rowId = String(r?._id || "");
      if (rowId) includedReceiptIds.add(rowId);

      const type = getReceiptType(r);
      if (!type) return;

      const paymentMode = String(
        r?.paymentMode || r?.modeOfPayment || r?.paymentType || r?.mode || "",
      )
        .trim()
        .toLowerCase();
      const toId = String(getToAccountIdFromRow(r) || "");
      const toName = getToAccountNameFromRow(r, accountsById);
      const fromId = resolveFromAccountId(r, accountsByName);

      const isCashToBankFlag = Boolean(r?.isCashToBank);
      const isBankToCashFlag = Boolean(r?.isBankToCash);
      const isSpecialCashTransferReceipt =
        specialCashReceiptIds.has(rowId) ||
        isSpecialCashToBankReceipt(r, baseDatabase);
      const hasBankDetails = Boolean(getToAccountIdFromRow(r));
      const particularAccountType = norm(r?.particularAccountType);
      const isCashModeEntry =
        paymentMode === "cash" ||
        (!paymentMode &&
          (particularAccountType === "cash" ||
            (!hasBankDetails && !isCashToBankFlag && !isBankToCashFlag)));

      if (
        !isCashModeEntry &&
        !isCashToBankFlag &&
        !isBankToCashFlag &&
        !isSpecialCashTransferReceipt
      ) {
        return;
      }

      let entryType = "";
      if (isCashToBankFlag || isSpecialCashTransferReceipt) {
        entryType = "cash_deposit_bank";
      } else if (isBankToCashFlag) {
        entryType = "cash_withdraw_bank";
      } else if (type === "receipt") {
        entryType = "receipt_cash";
      } else if (type === "payment") {
        entryType = "payment_cash";
      }

      if (!entryType) return;

      let particular = getParticularDisplay(r, accountsById, accountsByName);

      if (isCashToBankFlag || isSpecialCashTransferReceipt) {
        particular = `Cash Deposited in ${toName || "Bank"}`;
      } else if (isBankToCashFlag) {
        particular = `Cash Withdrawn from ${toName || "Bank"}`;
      }

      normalizedCashbookReceipts.push({
        date: getTransactionDate(r),
        type: entryType,
        voucherType:
          entryType === "cash_deposit_bank" ||
          entryType === "cash_withdraw_bank"
            ? "Contra"
            : type === "receipt"
              ? "Receipt"
              : "Payment",
        voucherNo: r?.voucherNo || "",
        particular,
        remark: r?.remark || r?.remarks || r?.description || "",
        paymentMode:
          entryType === "cash_deposit_bank" ||
          entryType === "cash_withdraw_bank"
            ? "Bank"
            : paymentMode === "cash"
              ? "Cash"
              : r?.paymentMode || "",
        amount: getTransactionAmount(r),
      });
    });

    const directSpecialCashEntries = [];

    for (const r of nextSpecialCashReceipts || []) {
      const rowId = String(r?._id || "");
      if (rowId && includedReceiptIds.has(rowId)) continue;
      if (!isSpecialCashToBankReceipt(r, baseDatabase)) continue;

      if (rowId) includedReceiptIds.add(rowId);

      const toName = getToAccountNameFromRow(r, accountsById);

      directSpecialCashEntries.push({
        date: getTransactionDate(r),
        type: "cash_deposit_bank",
        voucherType: "Receipt",
        voucherNo:
          r?.voucherNo ||
          r?.receiptNo ||
          r?.receiptNumber ||
          getInvoiceLikeNumber(r),
        particular: `Cash Deposited in ${toName || "Bank"}`,
        remark: r?.remark || r?.remarks || r?.description || "",
        paymentMode: "Bank",
        amount: getTransactionAmount(r),
        sourceId: rowId,
      });
    }

    const extraContraEntries = [];
    for (const r of nextBankReportRows || []) {
      const rowId = String(r?._id || "");
      if (rowId && includedReceiptIds.has(rowId)) continue;

      const type = getReceiptType(r);
      if (!type) continue;

      const toName = getToAccountNameFromRow(r, accountsById);

      const isCashToBankFlag = Boolean(r?.isCashToBank);
      const isBankToCashFlag = Boolean(r?.isBankToCash);
      const isSpecialCashTransferReceipt = isSpecialCashToBankReceipt(
        r,
        baseDatabase,
      );

      if (
        !isCashToBankFlag &&
        !isBankToCashFlag &&
        !isSpecialCashTransferReceipt
      )
        continue;

      let particular = getParticularDisplay(r, accountsById, accountsByName);

      if (isCashToBankFlag || isSpecialCashTransferReceipt) {
        particular = `Cash Deposited in ${toName || "Bank"}`;
      } else if (isBankToCashFlag) {
        particular = `Cash Withdrawn from ${toName || "Bank"}`;
      }

      extraContraEntries.push({
        date: getTransactionDate(r),
        type:
          isCashToBankFlag || isSpecialCashTransferReceipt
            ? "cash_deposit_bank"
            : "cash_withdraw_bank",
        voucherType: "Contra",
        voucherNo: r?.voucherNo || "",
        particular,
        remark: r?.remark || r?.remarks || r?.description || "",
        paymentMode: "Bank",
        amount: getTransactionAmount(r),
      });
    }

    const hasSeparatedCashbookData = Boolean(
      (nextCashBookData?.salesData || []).length ||
      (nextCashBookData?.purchaseData || []).length ||
      (nextCashBookData?.receipts || []).length,
    );

    const directEntries =
      !hasSeparatedCashbookData && Array.isArray(nextCashBookData?.directRows)
        ? nextCashBookData.directRows.map((row) =>
            normalizeDirectCashBookRow(row),
          )
        : [];

    const openingEntry = {
      date: "",
      type: nextCashOpening?.openingType === "credit" ? "credit" : "debit",
      voucherType: "Opening",
      voucherNo: "",
      particular: "Opening Balance",
      remark: "",
      paymentMode: "Cash",
      amount: toNum(nextCashOpening?.openingBalance),
      cashRunning:
        nextCashOpening?.openingType === "credit"
          ? -toNum(nextCashOpening?.openingBalance)
          : toNum(nextCashOpening?.openingBalance),
      isOpening: true,
    };

    const allTxnRows = [
      ...salesEntries,
      ...purchaseEntries,
      ...normalizedCashbookReceipts,
      ...directSpecialCashEntries,
      ...extraContraEntries,
      ...directEntries,
    ];

    allTxnRows.sort((a, b) => {
      const ad = new Date(a.date || "1970-01-01").getTime();
      const bd = new Date(b.date || "1970-01-01").getTime();
      if (ad !== bd) return ad - bd;
      const av = Number(a.voucherNo) || 0;
      const bv = Number(b.voucherNo) || 0;
      return av - bv;
    });

    const finalRows = [openingEntry, ...allTxnRows];

    finalRows.forEach((entry, i) => {
      if (i === 0) return;
      const prev = toNum(finalRows[i - 1].cashRunning);
      const debit = getDebitAmount(entry);
      const credit = getCreditAmount(entry);
      entry.cashRunning = prev + debit - credit;
    });

    return finalRows;
  };

  const safePostReport = async (api, database, body = {}) => {
    try {
      const response = await _Post(api, database, body);
      return response || {};
    } catch (e) {
      // A report may legitimately return 404 when that financial year has no data.
      // Never allow one optional report to clear data returned by the other APIs.
      console.warn("[CashBookReports] optional report skipped:", {
        api,
        database,
        status: e?.response?.status,
        message: e?.response?.data?.message || e?.message,
      });
      return {};
    }
  };

  const safeGetReport = async (api, database) => {
    try {
      const response = await _Get(api, database);
      return response || {};
    } catch (e) {
      console.warn("[CashBookReports] optional company details skipped:", {
        api,
        database,
        status: e?.response?.status,
        message: e?.response?.data?.message || e?.message,
      });
      return {};
    }
  };

  const extractBankReportRows = (bankRes) => {
    const root = unwrapApiResponse(bankRes);
    const nested =
      root?.BankAccountReport ||
      root?.bankAccountReport ||
      root?.result ||
      root?.report ||
      root;

    const rows = firstArray(
      root?.BankAccount,
      root?.bankAccount,
      root?.bankAccounts,
      nested?.BankAccount,
      nested?.bankAccount,
      nested?.bankAccounts,
      nested?.rows,
      nested?.data,
    );

    return rows.filter((row) => {
      const status = String(row?.status || "")
        .trim()
        .toLowerCase();
      return !status || status === "active" || status === "completed";
    });
  };

  const extractCashBookData = (cashRes) => {
    const root = unwrapApiResponse(cashRes);
    const cashBook =
      root?.CashBook ||
      root?.Cashbook ||
      root?.cashBook ||
      root?.cashbook ||
      root?.CashBookReport ||
      root?.cashBookReport ||
      root?.result ||
      root?.report ||
      root;

    return {
      salesData: firstArray(
        cashBook?.salesData,
        cashBook?.SalesData,
        cashBook?.sales,
        cashBook?.saleData,
        root?.salesData,
        root?.sales,
      ),
      purchaseData: firstArray(
        cashBook?.purchaseData,
        cashBook?.PurchaseData,
        cashBook?.purchases,
        cashBook?.purchase,
        root?.purchaseData,
        root?.purchases,
      ),
      receipts: firstArray(
        cashBook?.receipts,
        cashBook?.Receipts,
        cashBook?.receiptData,
        cashBook?.paymentsAndReceipts,
        cashBook?.transactions,
        root?.receipts,
        root?.receiptData,
        root?.transactions,
      ),
      directRows: firstArray(
        Array.isArray(root?.CashBook) ? root.CashBook : null,
        Array.isArray(root?.Cashbook) ? root.Cashbook : null,
        Array.isArray(root?.cashBook) ? root.cashBook : null,
        Array.isArray(root?.cashbook) ? root.cashbook : null,
        cashBook?.rows,
        cashBook?.entries,
        cashBook?.cashBookRows,
        Array.isArray(cashBook?.data) ? cashBook.data : null,
        root?.rows,
        root?.entries,
      ),
    };
  };

  const extractReceiptRows = (receiptRes) => {
    const root = unwrapApiResponse(receiptRes);

    return firstArray(
      root?.Receipts,
      root?.receipts,
      root?.Receipt,
      root?.receipt,
      root?.data?.Receipts,
      root?.data?.receipts,
      root?.result,
    );
  };

  const fetchSpecialCashReceipts = async (database, baseDatabase) => {
    try {
      const receiptRes = await _Get(View_Receipt, database);
      const receiptRows = extractReceiptRows(receiptRes);

      return receiptRows.filter((r) =>
        isSpecialCashToBankReceipt(r, baseDatabase),
      );
    } catch (e) {
      // Receipt API may legitimately have no data for a financial year.
      // Keep the cashbook page usable and simply return no special transfers.
      console.warn("[CashBookReports] special CASH receipts skipped:", {
        database,
        status: e?.response?.status,
        message: e?.response?.data?.message || e?.message,
      });
      return [];
    }
  };

  const fetchCashbookReportsForFY = async (db, fy) => {
    const database = buildDatabaseForFinancialYear(db, fy);
    const baseDatabase = getBaseDatabase(db);

    const [bankRes, cashRes, specialReceipts] = await Promise.all([
      safePostReport(BankAccount_Report, database, {}),
      safePostReport(Cashbook_Report, database, {}),
      fetchSpecialCashReceipts(database, baseDatabase),
    ]);

    return {
      bankRows: extractBankReportRows(bankRes),
      cashBookData: extractCashBookData(cashRes),
      specialCashReceipts: specialReceipts,
    };
  };

  const getCarryForwardOpeningForFY = async ({
    db,
    selectedFY,
    financialYears,
    nextCompanyBanks,
    baseOpening,
  }) => {
    const cleanSelectedFY = normalizeFinancialYear(selectedFY);
    const selectedStart = getFYStartYear(cleanSelectedFY);

    if (!db || !cleanSelectedFY || !selectedStart) return baseOpening;

    let years = Array.isArray(financialYears) ? financialYears : [];

    if (!years.length) {
      const prevFY = getPreviousFinancialYear(cleanSelectedFY);
      years = prevFY ? [prevFY, cleanSelectedFY] : [cleanSelectedFY];
    }

    years = normalizeFinancialYears(years).filter(
      (fy) => getFYStartYear(fy) < selectedStart,
    );

    if (!years.length) return baseOpening;

    let rollingOpening = baseOpening;

    for (const fy of years) {
      const {
        bankRows,
        cashBookData,
        specialCashReceipts: fySpecialCashReceipts,
      } = await fetchCashbookReportsForFY(db, fy);

      const fyRows = buildRowsWithRunning({
        nextCashBookData: cashBookData,
        nextBankReportRows: bankRows,
        nextCompanyBanks,
        nextCashOpening: rollingOpening,
        nextSpecialCashReceipts: fySpecialCashReceipts,
        baseDatabase: db,
      });

      const closingBalance = fyRows.length
        ? toNum(fyRows[fyRows.length - 1]?.cashRunning)
        : rollingOpening?.openingType === "credit"
          ? -toNum(rollingOpening?.openingBalance)
          : toNum(rollingOpening?.openingBalance);

      rollingOpening = balanceToOpening(closingBalance);
    }

    return rollingOpening;
  };

  const reload = async (range = null) => {
    const user = getUser();
    const db = user?.database;
    const selectedFY = getSelectedFinancialYear();
    const selectedDatabase = buildDatabaseForFinancialYear(db, selectedFY);

    if (!db) throw new Error("Missing database in userData");

    const inside = CheckPermission("cashbook Report");
    if (inside && typeof inside === "object") {
      setPermissions((prev) => ({ ...prev, ...inside }));
    }

    const companyUrl = `${View_Company_Details}${encodeURIComponent(user?._id)}/`;

    const [companyRes, bankRes, cashRes, directReceiptRes] = await Promise.all([
      safeGetReport(companyUrl, db),
      safePostReport(BankAccount_Report, selectedDatabase, {}),
      safePostReport(Cashbook_Report, selectedDatabase, {}),
      (async () => {
        try {
          return (await _Get(View_Receipt, selectedDatabase)) || {};
        } catch (e) {
          console.warn("[CashBookReports] receipt list skipped:", {
            database: selectedDatabase,
            status: e?.response?.status,
            message: e?.response?.data?.message || e?.message,
          });
          return {};
        }
      })(),
    ]);

    const companyRoot = unwrapApiResponse(companyRes);
    const nextCompanyDetail =
      companyRoot?.CompanyDetail ||
      companyRoot?.companyDetail ||
      companyRoot?.data?.CompanyDetail ||
      companyRoot?.data?.companyDetail ||
      {};
    const nextCompanyBanks = firstArray(
      nextCompanyDetail?.bankDetails,
      nextCompanyDetail?.BankDetails,
      nextCompanyDetail?.banks,
      companyRoot?.bankDetails,
    );

    const nextBankReportRows = extractBankReportRows(bankRes);
    const nextCashBookData = extractCashBookData(cashRes);
    const nextSpecialCashReceipts = extractReceiptRows(directReceiptRes).filter(
      (r) => isSpecialCashToBankReceipt(r, db),
    );

    console.log("[CashBookReports] loaded", {
      database: selectedDatabase,
      sales: nextCashBookData.salesData.length,
      purchases: nextCashBookData.purchaseData.length,
      receipts: nextCashBookData.receipts.length,
      specialCashToBankReceipts: nextSpecialCashReceipts.length,
      directRows: nextCashBookData.directRows.length,
      bankRows: nextBankReportRows.length,
    });

    // Do not call the old hard-coded 192.168.x.x URL here. That private URL
    // caused "Failed to fetch" and rejected the complete page load even though
    // the cashbook report API had already returned valid data.
    const baseOpening = extractCashOpening({
      cashResponse: cashRes,
      companyDetail: nextCompanyDetail,
      user,
    });

    const financialYears = getFinancialYearsForCarryForward(
      nextCompanyDetail,
      user,
    );

    // Same rule as ledger:
    // selected FY opening = previous FY closing.
    // If no previous FY data exists, it falls back to the cash master opening.
    const nextCashOpening = await getCarryForwardOpeningForFY({
      db,
      selectedFY,
      financialYears,
      nextCompanyBanks,
      baseOpening,
    });

    if (nextCompanyDetail && Object.keys(nextCompanyDetail).length) {
      setCompanyDetail(nextCompanyDetail);
      try {
        localStorage.setItem(
          "Companydetail",
          JSON.stringify(nextCompanyDetail),
        );
      } catch {}
    }

    setCompanyBanks(nextCompanyBanks);
    setBankReportRows(nextBankReportRows);
    setCashBookData(nextCashBookData);
    setCashOpening(nextCashOpening);

    let finalRows = buildRowsWithRunning({
      nextCashBookData,
      nextBankReportRows,
      nextCompanyBanks,
      nextCashOpening,
      nextSpecialCashReceipts,
      baseDatabase: db,
    });

    if (range && (range.startDate || range.endDate)) {
      finalRows = applyLocalDateFilter(finalRows, range);
      finalRows.forEach((entry, i) => {
        if (i === 0) return;
        const prev = toNum(finalRows[i - 1].cashRunning);
        const debit = getDebitAmount(entry);
        const credit = getCreditAmount(entry);
        entry.cashRunning = prev + debit - credit;
      });
    }

    setRows(finalRows);
    setAllRows(finalRows);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        await reload();
      } catch (e) {
        console.error("[CashBookReports] load error:", e);
        const message = e?.response?.data?.message || e?.message || "";
        setErrorMsg(
          message === "Missing database in userData"
            ? message
            : "Unable to prepare the cashbook report.",
        );
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;

    return rows.filter((r) =>
      visibleCols.some((c) => toStr(c.get(r)).toLowerCase().includes(term)),
    );
  }, [rows, search, visibleCols]);

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, r) => {
        if (!r?.isOpening) {
          acc.debit += getDebitAmount(r);
          acc.credit += getCreditAmount(r);
        }
        return acc;
      },
      { debit: 0, credit: 0 },
    );
  }, [filteredRows]);

  const closingBalance = filteredRows.length
    ? toNum(filteredRows[filteredRows.length - 1]?.cashRunning)
    : 0;

  const buildCashbookPdf = ({
    strapLogoDataUrl = null,
    mainLogoDataUrl = null,
  } = {}) => {
    const doc = new jsPDF("l", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();

    // Same top strap used by the ledger PDF.
    const strapY = 8;
    const leftText = "THIS CASH BOOK IS GENERATED IN";
    const rightText = "SOFT";

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);

    const leftWidth = doc.getTextWidth(leftText);
    const rightWidth = doc.getTextWidth(rightText);
    const strapLogoWidth = strapLogoDataUrl ? 14 : 0;
    const strapLogoHeight = strapLogoDataUrl ? 4 : 0;
    const strapGap = strapLogoDataUrl ? 1.5 : 0;
    const totalStrapWidth =
      leftWidth + strapLogoWidth + strapGap + rightWidth + 1;

    let strapX = (pageWidth - totalStrapWidth) / 2;
    doc.text(leftText, strapX, strapY);
    strapX += leftWidth + 1;

    if (strapLogoDataUrl) {
      doc.addImage(
        strapLogoDataUrl,
        "PNG",
        strapX,
        strapY - strapLogoHeight + 1,
        strapLogoWidth,
        strapLogoHeight,
      );
      strapX += strapLogoWidth + strapGap;
    }

    doc.text(rightText, strapX, strapY);

    let y = strapY + 6;

    if (mainLogoDataUrl) {
      const logoWidth = 45;
      const logoHeight = 16;
      doc.addImage(
        mainLogoDataUrl,
        "PNG",
        (pageWidth - logoWidth) / 2,
        y,
        logoWidth,
        logoHeight,
      );
      y += logoHeight + 2;
    }

    const companyName = getCompanyField(
      companyDetail,
      "name",
      "companyName",
      "CompanyName",
    );
    const address = getCompanyField(
      companyDetail,
      "address",
      "Address",
      "companyAddress",
    );
    const email = getCompanyField(
      companyDetail,
      "email",
      "Email",
      "companyEmail",
    );
    const mobile = getCompanyField(
      companyDetail,
      "mobileNo",
      "mobile",
      "phone",
      "contactNo",
    );

    if (companyName) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text(companyName, pageWidth / 2, y, { align: "center" });
      y += 6;
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);

    if (address) {
      const addressLines = doc.splitTextToSize(address, 230);
      addressLines.forEach((line) => {
        doc.text(line, pageWidth / 2, y, { align: "center" });
        y += 4.5;
      });
    }

    if (email) {
      doc.text(email, pageWidth / 2, y, { align: "center" });
      y += 4.5;
    }

    if (mobile) {
      doc.text(mobile, pageWidth / 2, y, { align: "center" });
      y += 4.5;
    }

    const selectedFY = getSelectedFinancialYear();
    const title = `Cash Book${selectedFY ? ` (${selectedFY})` : ""}`;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(title, pageWidth / 2, y + 4, { align: "center" });
    y += 9;

    if (startDate || endDate) {
      const periodText = `Period: ${startDate || "Beginning"} to ${
        endDate || "Current"
      }`;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text(periodText, pageWidth / 2, y, { align: "center" });
      y += 5;
    }

    const pdfRows = filteredRows.map((row) => [
      row?.date || "",
      row?.particular
        ? `${row.particular}${row?.remark ? ` - ${row.remark}` : ""}`
        : "",
      row?.voucherType || "",
      row?.voucherNo || "",
      row?.paymentMode || "",
      fmtAmt(getDebitAmount(row)),
      fmtAmt(getCreditAmount(row)),
      formatBalanceText(row?.cashRunning),
    ]);

    const columnStyles = {
      0: { cellWidth: 24, halign: "center" },
      1: { cellWidth: 76, halign: "left" },
      2: { cellWidth: 31, halign: "center" },
      3: { cellWidth: 24, halign: "center" },
      4: { cellWidth: 21, halign: "center" },
      5: { cellWidth: 29, halign: "right" },
      6: { cellWidth: 29, halign: "right" },
      7: { cellWidth: 43, halign: "right" },
    };

    autoTable(doc, {
      startY: y + 2,
      head: [
        [
          "Date",
          "Particular",
          "Voucher Type",
          "Voucher No",
          "Mode",
          "Debit",
          "Credit",
          "Balance",
        ],
      ],
      body: pdfRows,
      theme: "grid",
      headStyles: {
        fillColor: [22, 160, 133],
        textColor: [255, 255, 255],
        halign: "center",
        fontStyle: "bold",
      },
      bodyStyles: { textColor: [15, 23, 42] },
      styles: {
        fontSize: 8.5,
        cellPadding: 1.7,
        overflow: "linebreak",
        valign: "middle",
      },
      margin: { left: 10, right: 10 },
      tableWidth: "auto",
      columnStyles,
      didParseCell: (data) => {
        const sourceRow = filteredRows[data.row.index];
        if (data.section === "body" && sourceRow?.isOpening) {
          data.cell.styles.fillColor = [254, 252, 232];
          data.cell.styles.fontStyle = "bold";
        }
      },
    });

    const summaryY =
      doc.lastAutoTable?.finalY != null
        ? Number(doc.lastAutoTable.finalY) + 4
        : y + 20;

    autoTable(doc, {
      startY: summaryY,
      body: [
        [
          "",
          "Closing Balance",
          "",
          "",
          "",
          "",
          "",
          formatBalanceText(closingBalance),
        ],
        [
          "",
          "Total",
          "",
          "",
          "",
          totals.debit.toFixed(2),
          totals.credit.toFixed(2),
          formatBalanceText(closingBalance),
        ],
      ],
      theme: "grid",
      styles: {
        fontSize: 8.5,
        cellPadding: 1.7,
        fontStyle: "bold",
      },
      margin: { left: 10, right: 10 },
      tableWidth: "auto",
      columnStyles,
    });

    return doc;
  };

  const handleExportPDF = async () => {
    try {
      if (!filteredRows.length) {
        swal("No cashbook records available to download");
        return;
      }

      const strapLogoDataUrl = await loadImageAsDataUrl(RupioSoftLogo);
      const companyLogoUrl = getCompanyLogoUrl(companyDetail);
      const mainLogoDataUrl = companyLogoUrl
        ? await loadImageAsDataUrl(companyLogoUrl)
        : null;

      const doc = buildCashbookPdf({
        strapLogoDataUrl,
        mainLogoDataUrl,
      });

      const selectedFY = getSelectedFinancialYear();
      const periodSuffix =
        startDate || endDate
          ? `-${startDate || "start"}-to-${endDate || "end"}`
          : "";
      const fileName = `CashbookReport${
        selectedFY ? `-${selectedFY}` : ""
      }${periodSuffix}.pdf`;

      doc.save(fileName);
    } catch (error) {
      console.error("[CashBookReports] PDF download failed:", error);
      swal("Failed to download cashbook PDF");
    }
  };

  const handleExportXLSX = async (fileName) => {
    const cols = visibleCols;

    const data = filteredRows.map((r) => {
      const obj = {};
      cols.forEach((c) => {
        obj[c.header] = toStr(c.get(r));
      });
      return obj;
    });

    const totalObj = {};
    cols.forEach((c) => {
      if (c.key === "particular") totalObj[c.header] = "TOTAL";
      else if (c.key === "debit") totalObj[c.header] = totals.debit.toFixed(2);
      else if (c.key === "credit")
        totalObj[c.header] = totals.credit.toFixed(2);
      else if (c.key === "balance")
        totalObj[c.header] = formatBalanceText(closingBalance);
      else totalObj[c.header] = "";
    });
    data.push(totalObj);

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Cash Book");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `${fileName}.xlsx`,
    );
  };

  const submitDate = async () => {
    try {
      setLoading(true);
      await reload({ startDate, endDate });
    } catch (e) {
      console.error(e);
      swal("Failed to filter by dates");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckbox = (checked, colKey) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(colKey)) buf.push(colKey);
    } else {
      const i = buf.indexOf(colKey);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };

  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current]),
    );
    setSelectedKeys(next);
  };

  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };

  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };

  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };

  const applyColumns = () => {
    localStorage.setItem("CashbookCols", JSON.stringify(selectedKeys));
    closeModal();
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filteredRows?.length;
  const hasOpeningRow = filteredRows.some((r) => r?.isOpening);
  const hasTransactionRows = filteredRows.some((r) => !r?.isOpening);

  return (
    <>
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      <Row className="cb-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            style={{
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Cashbook Report{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
              </div>

              <div style={{ flex: "1 1 240px" }} />

              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div>
                  <Label className="m-0" style={{ fontSize: 12 }}>
                    From
                  </Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ height: 32 }}
                  />
                </div>
                <div>
                  <Label className="m-0" style={{ fontSize: 12 }}>
                    To
                  </Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ height: 32 }}
                  />
                </div>
                <Button
                  color="primary"
                  style={{ height: 32 }}
                  onClick={submitDate}
                >
                  Submit
                </Button>
              </div>

              <Button
                outline
                color="primary"
                onClick={openModal}
                title="Filter Columns"
                style={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaFilter size={14} />
              </Button>

              {permissions?.Download && (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <Button
                    outline
                    color="danger"
                    className="cb-export-btn"
                    onClick={handleExportPDF}
                    disabled={!filteredRows.length}
                    title="Download formatted PDF"
                  >
                    <AiOutlineFilePdf size={17} />
                    PDF
                  </Button>

                  <Button
                    outline
                    color="success"
                    className="cb-export-btn"
                    onClick={() => handleExportXLSX("CashbookReport")}
                    disabled={!filteredRows.length}
                    title="Download Excel list"
                  >
                    <ImDownload size={15} />
                    Excel
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No records found.
              </div>
            ) : (
              <div className="cb-table-wrap">
                <table className="cb-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {filteredRows.map((r, idx) => (
                      <tr
                        key={idx}
                        className={r.isOpening ? "opening-row" : ""}
                      >
                        {visibleCols.map((c) => (
                          <td key={c.key} className={c.tdClass || ""}>
                            {toStr(c.get(r))}
                          </td>
                        ))}
                      </tr>
                    ))}

                    {hasOpeningRow && !hasTransactionRows && (
                      <tr>
                        <td
                          colSpan={visibleCols.length || 1}
                          style={{
                            padding: 18,
                            textAlign: "center",
                            color: "#6b7280",
                            fontWeight: 700,
                            background: "#fff",
                          }}
                        >
                          No transaction records found for this financial year.
                        </td>
                      </tr>
                    )}
                  </tbody>

                  <tfoot>
                    <tr className="total-row">
                      {visibleCols.map((c) => {
                        if (c.key === "particular") {
                          return (
                            <td key={c.key} className="align-left">
                              TOTAL
                            </td>
                          );
                        }
                        if (c.key === "debit") {
                          return (
                            <td key={c.key} className="big-num align-right">
                              {totals.debit.toFixed(2)}
                            </td>
                          );
                        }
                        if (c.key === "credit") {
                          return (
                            <td key={c.key} className="big-num align-right">
                              {totals.credit.toFixed(2)}
                            </td>
                          );
                        }
                        if (c.key === "balance") {
                          return (
                            <td key={c.key} className="big-num align-right">
                              {formatBalanceText(closingBalance)}
                            </td>
                          );
                        }
                        return <td key={c.key}></td>;
                      })}
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        isOpen={modal}
        toggle={closeModal}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeModal}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>
              <div className="listbox">
                {baseColumns.map((col) => (
                  <div key={col.key} className="pick">
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12" xl="8" xs="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div className="listbox" style={{ flex: 1 }}>
                  {selectedKeys.map((key, i) => {
                    const col = baseColumns.find((c) => c.key === key);
                    if (!col) return null;
                    return (
                      <div
                        key={key}
                        className="pick"
                        style={{
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>

                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>

              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
}
